<template>
  <Teleport to="body">
    <dt-modal
      v-model:show="isOpen"
      @close="handleClose"
      :close-button-props="{ ariaLabel: 'Close' }"
      :showFooter="false"
      :fixed-header-footer="true"
      :dialogClass="modalDialogClasses"
      contentClass="d-w100p d-h100p d-of-hidden"
    >
      <template #header>
        <div class="d-px32 d-py24 d-bb d-bc-subtle">
          <h1 class="dp3-fs-350 d-lh-100">Preferences</h1>
        </div>
      </template>

      <div class="d-d-flex d-h100p">
        <div class="d-d-flex d-fl0 d-wmx216 d-w100p d-p24 d-pr12">
          <dt-stack gap="200" class="d-w100p">
            <dt-button
              v-for="section in preferenceSections"
              :key="section.id"
              importance="clear"
              kind="muted"
              @click="setActiveSection(section.id)"
              class="d-w100p dp3-modal--nav-item"
              :class="{
                'd-bgc-brand-strong h:d-bgc-brand-strong d-fc-primary-inverted dp3-modal--nav-item__active':
                  activeSection === section.id,
              }"
              :label-class="['d-jc-unset dp3-fs-150', activeSection === section.id ? 'd-fw-semibold' : 'd-fw-medium']"
            >
              {{ section.name }}
            </dt-button>
          </dt-stack>
        </div>
        <div class="d-d-flex d-fl1 d-w100p">
          <!-- Dynamic content based on active section -->
          <div
            v-if="currentSection"
            class="d-d-flex d-fd-column d-p24 d-pl12 d-w100p d-h100p dp3-modal--content d-of-y-auto"
          >
            <!-- Static content sections -->
            <template v-if="currentSection.content.type === 'static'">
              <p class="d-fs-200">{{ currentSection.content.description }}</p>
            </template>

            <!-- Component-based sections -->
            <template v-else-if="currentSection.content.type === 'component'">
              <AppearanceSettings v-if="currentSection.content.component === 'AppearanceSettings'" />
              <!-- Future components will be added here -->
              <!-- <notification-settings v-else-if="currentSection.content.component === 'NotificationSettings'" /> -->
              <!-- <hardware-settings v-else-if="currentSection.content.component === 'HardwareSettings'" /> -->
              <!-- <accessibility-settings v-else-if="currentSection.content.component === 'AccessibilitySettings'" /> -->
              <!-- <other-settings v-else-if="currentSection.content.component === 'OtherSettings'" /> -->

              <!-- 
                Future enhancement: Dynamic component loading
                <component :is="currentSection.content.component" v-else />
                This would require importing components dynamically or using a component registry
              -->
            </template>
          </div>
        </div>
      </div>
    </dt-modal>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed, Teleport } from 'vue';
import { DtModal, DtStack, DtButton } from '@dialpad/dialtone/vue3';
import AppearanceSettings from './AppearanceSettings.vue';

// Helper function to create preference sections
const createPreferenceSection = (id, name, content) => ({
  id,
  name,
  content,
});

// Helper function to create static content
const createStaticContent = description => ({
  type: 'static',
  description,
});

// Helper function to create component content
const createComponentContent = component => ({
  type: 'component',
  component,
});

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['update:modelValue']);

// Reactive state
const activeSection = ref('notifications');

// Computed for modal dialog classes
const modalDialogClasses = computed(() => {
  const baseClasses = 'd-gg0 d-p0 d-w100p d-wmx764 d-h100p d-hmx628';
  const appearanceClasses = activeSection.value === 'theme' ? ' d-bs-none' : '';
  return baseClasses + appearanceClasses;
});

// Computed for v-model
const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

// Computed for current section content
const currentSection = computed(() => {
  return preferenceSections.find(section => section.id === activeSection.value);
});

/**
 * Preference sections configuration
 *
 * To add a new section:
 * 1. For simple content: createPreferenceSection('id', 'Name', createStaticContent('Description'))
 * 2. For component: createPreferenceSection('id', 'Name', createComponentContent('ComponentName'))
 *
 * Example future additions:
 * createPreferenceSection('privacy', 'Privacy', createComponentContent('PrivacySettings')),
 * createPreferenceSection('integrations', 'Integrations', createStaticContent('Connect with external services.'))
 *
 * Adding a new section is as simple as:
 * 1. Add the section to this array
 * 2. For components: Add the v-if condition in the template
 * 3. For components: Import the component at the top
 */
const preferenceSections = [
  createPreferenceSection(
    'notifications',
    'Notifications',
    createStaticContent('Configure your notification preferences and sound settings.')
  ),
  createPreferenceSection(
    'hardware',
    'Hardware',
    createStaticContent('Configure your microphone, camera, and other hardware settings.')
  ),
  createPreferenceSection(
    'accessibility',
    'Accessibility',
    createStaticContent('Configure accessibility settings to make Dialpad work better for you.')
  ),
  createPreferenceSection('theme', 'Appearance', createComponentContent('AppearanceSettings')),
  createPreferenceSection('other', 'Other Settings', createStaticContent('Additional settings and preferences.')),
];

// Methods
function handleClose() {
  isOpen.value = false;
  // Reset modal backdrop color when closing the modal
  document.documentElement.style.setProperty('--dt-color-surface-backdrop', '');
}

function setActiveSection(sectionId) {
  activeSection.value = sectionId;

  // Set modal backdrop color based on active section
  if (sectionId === 'theme') {
    // Make backdrop transparent for Appearance tab
    document.documentElement.style.setProperty('--dt-color-surface-backdrop', 'transparent');
  } else {
    // Reset to default for other tabs
    document.documentElement.style.setProperty('--dt-color-surface-backdrop', '');
  }
}

// Watch for modal opening to set initial backdrop
watch(isOpen, newValue => {
  if (newValue) {
    // Set initial backdrop color based on active section
    if (activeSection.value === 'theme') {
      // Make backdrop transparent for Appearance tab
      document.documentElement.style.setProperty('--dt-color-surface-backdrop', 'transparent');
    } else {
      // Reset to default for other tabs
      document.documentElement.style.setProperty('--dt-color-surface-backdrop', '');
    }
  } else {
    // Reset backdrop when closing
    document.documentElement.style.setProperty('--dt-color-surface-backdrop', '');

    // Blur any focused elements to prevent aria-hidden accessibility warning
    if (document.activeElement) {
      document.activeElement.blur();
    }
  }
});
</script>

<style lang="less"></style>
